import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { 
  Calendar, MapPin, Users, CreditCard, Building2, 
  Phone, Mail, FileText, Clock, CheckCircle, XCircle, AlertCircle,
  Plane, Hotel, Car, ArrowLeft, User, Briefcase, ListOrdered, Moon,
  ScrollText, MessageSquare, FileDown, Info, Edit3, Save, X, Banknote, Receipt, Send, PiggyBank, History, Percent, Calculator
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import generateQuotePdfDoc from "../utils/quotePdf";
import DashboardLayout from "../components/layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import InvoiceEditor from "./InvoiceEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;
  return `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}/${date.getFullYear()}`;
};

const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  return `₹${amount.toLocaleString('en-IN')}`;
};

const parseDayDate = (dayDateStr, travelDate, dayIndex) => {
  if (dayDateStr) {
    const isoDate = new Date(dayDateStr + 'T00:00:00');
    if (!isNaN(isoDate.getTime())) return isoDate;
    const parsed = new Date(dayDateStr);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  if (travelDate) {
    const base = new Date(travelDate + 'T00:00:00');
    if (!isNaN(base.getTime())) { const d = new Date(base); d.setDate(d.getDate() + dayIndex); return d; }
  }
  return null;
};

const formatDayDateLabel = (date) => {
  if (!date) return '';
  return `${date.toLocaleDateString('en-US',{weekday:'short'}).toUpperCase()} - ${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}/${date.getFullYear()}`;
};

const BookingDetail = () => {
  const { id } = useParams();
  const { token, user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [editingGuest, setEditingGuest] = useState(false);
  const [guestForm, setGuestForm] = useState({ guest_name: '', guest_mobile: '', guest_email: '' });
  const [savingGuest, setSavingGuest] = useState(false);
  const [editingConf, setEditingConf] = useState(false);
  const [confForm, setConfForm] = useState([]);
  const [savingConf, setSavingConf] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [showInvoiceEditor, setShowInvoiceEditor] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [profitData, setProfitData] = useState(null);
  const [vendorSummaries, setVendorSummaries] = useState([]);
  const [agentMarkup, setAgentMarkup] = useState(null);
  const [downloadEnabled, setDownloadEnabled] = useState(false);
  const [togglingDownload, setTogglingDownload] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [savingFollowup, setSavingFollowup] = useState(false);
  const [followupNote, setFollowupNote] = useState("");
  const [savingGstRate, setSavingGstRate] = useState(false);
  // Flight & Hotel edit state
  const [editingFlights, setEditingFlights] = useState(false);
  const [flightForm, setFlightForm] = useState([]);
  const [savingFlights, setSavingFlights] = useState(false);
  const [editingHotels, setEditingHotels] = useState(false);
  const [hotelForm, setHotelForm] = useState([]);
  const [savingHotels, setSavingHotels] = useState(false);
  // Editable markup form (agent only). Persists to the linked quote via /quotes/{id}/agent-update.
  const [markupForm, setMarkupForm] = useState({ mode: 'manual', percentage: 0, adult: 0, cwb: 0, cnb: 0 });
  const [savingMarkup, setSavingMarkup] = useState(false);

  useEffect(() => { fetchBookingDetail(); }, [id]);

  const fetchBookingDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bookings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const b = response.data;
      setBooking(b);
      setDownloadEnabled(!!b.agent_download_enabled);
      const q = b.quote_details || {};
      setGuestForm({
        guest_name: b.guest_name || q.client_name || '',
        guest_mobile: b.guest_mobile || b.client_phone || q.client_phone || '',
        guest_email: b.guest_email || b.client_email || q.client_email || ''
      });
      // Fetch payments (admin + agent both see Payment Detail)
      try {
        const pRes = await axios.get(`${API}/payments?booking_id=${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setPayments(pRes.data || []);
      } catch { setPayments([]); }
      // Fetch payments
      if (user?.role === 'admin') {
        // Profit snapshot (admin only)
        try {
          const profRes = await axios.get(`${API}/bookings/${id}/profit`, { headers: { Authorization: `Bearer ${token}` } });
          setProfitData(profRes.data);
        } catch { setProfitData(null); }
        // Vendor payment summaries (admin only) — for Vendor Detail card
        try {
          const vRes = await axios.get(`${API}/vendor-payments/by-booking/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          setVendorSummaries(Array.isArray(vRes.data) ? vRes.data : []);
        } catch { setVendorSummaries([]); }
        // Follow-up log (admin only)
        try {
          const fuRes = await axios.get(`${API}/bookings/${id}/followups`, { headers: { Authorization: `Bearer ${token}` } });
          setFollowups(fuRes.data?.data || []);
        } catch { setFollowups([]); }
      }
      // Agent markup (agent sees this instead of profit snapshot)
      if (user?.role === 'agent') {
        try {
          const mkRes = await axios.get(`${API}/bookings/${id}/agent-markup`, { headers: { Authorization: `Bearer ${token}` } });
          setAgentMarkup(mkRes.data);
        } catch { setAgentMarkup(null); }
        // Seed the editable markup form from the linked quote.
        setMarkupForm({
          mode: q.markup_mode || 'manual',
          percentage: Number(q.markup_percentage || 0),
          adult: Number(q.markup_per_adult || 0),
          cwb: Number(q.markup_per_cwb || 0),
          cnb: Number(q.markup_per_cnb || 0),
        });
      }
      // Fetch invoice
      try {
        const invRes = await axios.get(`${API}/bookings/${id}/invoice`, { headers: { Authorization: `Bearer ${token}` } });
        if (invRes.data?.exists) setInvoice(invRes.data.invoice);
        else {
          // Auto-generate if tour completed
          const rd = b.return_date || (b.quote_details?.return_date);
          if (rd && new Date(rd) < new Date() && b.status === 'confirmed') {
            try {
              const genRes = await axios.post(`${API}/bookings/${id}/invoice/generate`, {}, { headers: { Authorization: `Bearer ${token}` } });
              if (genRes.data?.invoice) setInvoice(genRes.data.invoice);
            } catch {}
          }
        }
      } catch {}
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };

  const saveGuestInfo = async () => {
    try {
      setSavingGuest(true);
      await axios.put(`${API}/bookings/${id}/guest-info`, guestForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Guest information updated!");
      setEditingGuest(false);
      fetchBookingDetail();
    } catch (err) {
      toast.error("Failed to update guest info");
    } finally { setSavingGuest(false); }
  };

  const saveConfirmations = async () => {
    try {
      setSavingConf(true);
      await axios.put(`${API}/bookings/${id}/confirmations`, confForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Confirmation numbers updated!");
      setEditingConf(false);
      fetchBookingDetail();
    } catch (err) {
      toast.error("Failed to update confirmations");
    } finally { setSavingConf(false); }
  };

  // Save agent markup back to the LINKED QUOTE so it's reflected in the
  // booking's profit snapshot, agent total amount, payment math and PDFs.
  const saveAgentMarkup = async () => {
    const qid = booking?.quote_id || booking?.quote_details?.id;
    if (!qid) { toast.error("Linked quote not found"); return; }
    try {
      setSavingMarkup(true);
      const payload = {
        markup_mode: markupForm.mode,
        markup_percentage: markupForm.mode === "percentage" ? Number(markupForm.percentage || 0) : 0,
        markup_per_adult: markupForm.mode === "manual" ? Number(markupForm.adult || 0) : 0,
        markup_per_cwb: markupForm.mode === "manual" ? Number(markupForm.cwb || 0) : 0,
        markup_per_cnb: markupForm.mode === "manual" ? Number(markupForm.cnb || 0) : 0,
      };
      await axios.put(`${API}/quotes/${qid}/agent-update`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Markup saved!");
      fetchBookingDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save markup");
    } finally {
      setSavingMarkup(false);
    }
  };

  const openFlightEditor = () => {
    const current = (Array.isArray(booking?.flight_details_override) && booking.flight_details_override.length > 0)
      ? booking.flight_details_override
      : (((booking?.quote_details || {}).show_flight_details && Array.isArray((booking?.quote_details || {}).flight_details))
          ? (booking?.quote_details || {}).flight_details : []);
    setFlightForm(current.length > 0 ? current.map(f => ({
      airline: f.airline || '', flight_number: f.flight_number || '',
      from: f.from || f.origin || '', to: f.to || f.destination || '',
      date: f.date || '', time: f.time || f.departure_time || '',
      pnr: f.pnr || ''
    })) : [{ airline: '', flight_number: '', from: '', to: '', date: '', time: '', pnr: '' }]);
    setEditingFlights(true);
  };

  const saveFlightDetails = async () => {
    try {
      setSavingFlights(true);
      const cleaned = (flightForm || []).filter(f =>
        (f.airline || f.flight_number || f.from || f.to || f.date || f.time || f.pnr || '').toString().trim() !== ''
      );
      await axios.put(`${API}/bookings/${id}/flight-details`, { flight_details: cleaned }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Flight details updated!");
      setEditingFlights(false);
      fetchBookingDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update flight details");
    } finally { setSavingHotels && setSavingFlights(false); }
  };

  const openHotelEditor = () => {
    const current = (Array.isArray(booking?.hotels_override) && booking.hotels_override.length > 0)
      ? booking.hotels_override
      : hotels;
    setHotelForm(current.length > 0 ? current.map(h => ({
      hotel_name: h.hotel_name || h.name || '',
      city: h.city || '',
      hotel_category: h.hotel_category || '',
      room_type: h.room_type || h.room_category || 'Standard',
      meal_plan: h.meal_plan || '',
      nights: h.nights || 1,
      check_in: h.check_in || '',
      check_out: h.check_out || '',
      rooms_count: h.rooms_count || h.rooms || 1,
      extra_bed_toggle: !!h.extra_bed_toggle,
      extra_bed_count: h.extra_bed_count || 0,
      cwb_toggle: !!h.cwb_toggle,
      cwb_count: h.cwb_count || 0,
      cnb_toggle: !!h.cnb_toggle,
      cnb_count: h.cnb_count || 0,
      confirmation_number: h.confirmation_number || ''
    })) : [{
      hotel_name: '', city: '', hotel_category: '', room_type: 'Standard',
      meal_plan: '', nights: 1, check_in: '', check_out: '',
      rooms_count: 1, extra_bed_toggle: false, extra_bed_count: 0,
      cwb_toggle: false, cwb_count: 0, cnb_toggle: false, cnb_count: 0,
      confirmation_number: ''
    }]);
    setEditingHotels(true);
  };

  const saveHotelDetails = async () => {
    try {
      setSavingHotels(true);
      const cleaned = (hotelForm || []).filter(h => (h.hotel_name || h.city || '').toString().trim() !== '');
      await axios.put(`${API}/bookings/${id}/hotel-details`, { hotels: cleaned }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Hotel details updated!");
      setEditingHotels(false);
      fetchBookingDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update hotel details");
    } finally { setSavingHotels(false); }
  };

  const handleGenerateInvoice = async () => {
    try {
      setGeneratingInvoice(true);
      const res = await axios.post(`${API}/bookings/${id}/invoice/generate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.invoice) { setInvoice(res.data.invoice); toast.success("Invoice generated!"); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate invoice");
    } finally { setGeneratingInvoice(false); }
  };

  const handleSendInvoiceToAgent = async () => {
    try {
      setSendingInvoice(true);
      await axios.put(`${API}/bookings/${id}/invoice/send-to-agent`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setInvoice(prev => ({ ...prev, sent_to_agent: true }));
      toast.success("Invoice sent to agent successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invoice to agent");
    } finally { setSendingInvoice(false); }
  };

  const handleDownloadInvoice = async () => {
    try {
      setInvoiceLoading(true);
      const res = await axios.get(`${API}/bookings/${id}/invoice/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoice?.invoice_number || 'download'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded!");
    } catch (err) {
      toast.error("Failed to download invoice");
    } finally { setInvoiceLoading(false); }
  };

  // Admin toggles whether agent can download/share voucher
  const handleToggleAgentDownload = async (next) => {
    setTogglingDownload(true);
    try {
      await axios.patch(
        `${API}/bookings/${id}/download-permission?enabled=${next}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDownloadEnabled(next);
      toast.success(next ? "Agent can now download & share voucher" : "Agent download disabled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update permission");
    } finally {
      setTogglingDownload(false);
    }
  };

  // Share booking voucher link on WhatsApp (agent side)
  const handleShareVoucherOnWhatsApp = () => {
    const bno = booking?.booking_number || booking?.id?.slice(0, 8).toUpperCase();
    const dest = booking?.destination || '';
    const td = booking?.travel_date || '';
    const msg =
      `Hi! Please find your Happy Holidays DMC booking details below:%0A%0A` +
      `Booking: #${bno}%0A` +
      `Destination: ${dest}%0A` +
      `Travel Date: ${td}%0A%0A` +
      `Booking voucher is attached. For any queries, contact your travel advisor.`;
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  // Admin: update GST rate for this booking
  const handleGstRateChange = async (newRateFraction) => {
    setSavingGstRate(true);
    try {
      await axios.patch(
        `${API}/bookings/${id}/gst-rate?rate=${newRateFraction}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Re-fetch profit snapshot with new rate
      const profRes = await axios.get(`${API}/bookings/${id}/profit`, { headers: { Authorization: `Bearer ${token}` } });
      setProfitData(profRes.data);
      toast.success(`GST rate updated to ${Math.round(newRateFraction * 100)}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update GST rate");
    } finally {
      setSavingGstRate(false);
    }
  };

  // Admin: add a manual follow-up entry
  const handleAddFollowup = async (ftype) => {
    if (ftype === 'note' && !followupNote.trim()) {
      toast.error("Please enter a note");
      return;
    }
    setSavingFollowup(true);
    try {
      await axios.post(
        `${API}/bookings/${id}/followups`,
        { type: ftype, note: followupNote.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const fuRes = await axios.get(`${API}/bookings/${id}/followups`, { headers: { Authorization: `Bearer ${token}` } });
      setFollowups(fuRes.data?.data || []);
      setFollowupNote("");
      toast.success("Follow-up logged");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log follow-up");
    } finally {
      setSavingFollowup(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = { pending: { bg: "bg-amber-100 text-amber-700", icon: Clock }, confirmed: { bg: "bg-emerald-100 text-emerald-700", icon: CheckCircle }, cancelled: { bg: "bg-red-100 text-red-700", icon: XCircle } };
    const config = styles[status] || { bg: "bg-gray-100 text-gray-700", icon: AlertCircle };
    const Icon = config.icon;
    return <Badge className={`${config.bg} flex items-center gap-1 px-3 py-1`}><Icon className="w-3.5 h-3.5" /><span className="capitalize">{status}</span></Badge>;
  };

  const getPaymentBadge = (status) => {
    const styles = { pending: "bg-amber-100 text-amber-700", partial: "bg-orange-100 text-orange-700", full: "bg-emerald-100 text-emerald-700" };
    return <Badge className={styles[status] || "bg-gray-100 text-gray-700"}>Payment: <span className="capitalize ml-1">{status}</span></Badge>;
  };

  const dashboardType = user?.role === 'admin' ? 'admin' : 'agent';

  if (loading) return <DashboardLayout type={dashboardType}><div className="flex justify-center items-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" /></div></DashboardLayout>;
  if (error) return <DashboardLayout type={dashboardType}><div className="flex flex-col items-center justify-center py-20"><XCircle className="w-16 h-16 text-red-400 mb-4" /><h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Booking</h2><p className="text-gray-500">{error}</p><Button variant="outline" className="mt-4" onClick={() => window.history.back()}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button></div></DashboardLayout>;
  if (!booking) return null;

  const quote = booking.quote_details || {};
  const accOpts = quote.accommodation_options || [];
  const selOpt = quote.selected_option || 0;
  // Hotels override (saved on booking) takes precedence so agent edits surface
  let hotels = Array.isArray(booking.hotels_override) && booking.hotels_override.length > 0
    ? booking.hotels_override
    : ((accOpts.length > 0 && accOpts[selOpt]) ? (accOpts[selOpt].hotels || []) : []);
  if (hotels.length === 0) hotels = quote.hotels || [];
  const itinerary = quote.itinerary || booking.itinerary || [];
  // Flight override (saved on booking) takes precedence over the original quote-level flight_details
  const flightDetails = Array.isArray(booking.flight_details_override) && booking.flight_details_override.length > 0
    ? booking.flight_details_override
    : ((quote.show_flight_details && Array.isArray(quote.flight_details)) ? quote.flight_details : []);
  const confirmations = booking.confirmations || [];
  // Per-row (positional) match: hotels[idx] ↔ confirmations[idx].
  // Each hotel row keeps its OWN confirmation number even when the hotel name repeats.
  // Fallback to legacy name/city match only when positional data is missing.
  const legacyConfMap = {};
  confirmations.forEach(c => { if (c.hotel) legacyConfMap[c.hotel] = c.confirmation_number || ''; });
  const travelDate = quote.travel_date || booking.travel_date;
  const returnDate = quote.return_date || booking.return_date;
  const nights = quote.nights || (travelDate && returnDate ? Math.ceil((new Date(returnDate) - new Date(travelDate)) / 86400000) : 0);
  const bookingNo = booking.booking_number || booking.id.slice(0, 8).toUpperCase();
  const isAdmin = user?.role === 'admin';
  // Agent can edit hotel block ONLY when the quote was Land-Only
  // Agent can edit hotel block ONLY when the quote is Land-Only.
  // We check multiple legacy/new flags to be robust:
  //   • with_hotel === false / 'No'  → explicit Land-Only flag
  //   • hotel_enabled === false      → legacy field name
  //   • Both flags absent AND no hotels saved → infer Land-Only
  const _wh = quote.with_hotel;
  const _he = quote.hotel_enabled;
  const _isLandOnly =
    _wh === false || _wh === 'No' || _wh === 'no' ||
    _he === false ||
    (_wh === undefined && _he === undefined && hotels.length === 0);
  const canAgentEditHotels = !isAdmin && _isLandOnly;

  // Build hotel lookup by city for itinerary
  const hotelByCity = {};
  hotels.forEach(h => {
    const city = (h.city || '').toLowerCase();
    if (city && !hotelByCity[city]) hotelByCity[city] = h;
  });

  return (
    <DashboardLayout type={dashboardType}>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="booking-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="font-mono text-emerald-600 font-bold bg-emerald-50 px-3 py-1.5 rounded-lg text-lg">#{bookingNo}</span>
              {getStatusBadge(booking.status)}
              {getPaymentBadge(booking.payment_status)}
              {/* Invoice Status pill — admin-only nudge */}
              {isAdmin && (() => {
                if (!invoice) {
                  return (
                    <button
                      onClick={() => {
                        const el = document.querySelector('[data-testid="invoice-section"]');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-xs font-semibold hover:bg-amber-200 transition-colors"
                      title="Click to scroll to Invoice section"
                      data-testid="invoice-status-pill"
                    >
                      <Receipt className="w-3.5 h-3.5" />
                      Invoice Not Yet Generated
                    </button>
                  );
                }
                if (invoice.sent_to_agent) {
                  return (
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs font-semibold"
                      title={`Invoice ${invoice.invoice_number} sent to agent`}
                      data-testid="invoice-status-pill"
                    >
                      <Receipt className="w-3.5 h-3.5" />
                      Invoice {invoice.invoice_number} · Sent to Agent
                    </span>
                  );
                }
                return (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200 text-xs font-semibold"
                    title={`Invoice ${invoice.invoice_number} generated · not yet sent`}
                    data-testid="invoice-status-pill"
                  >
                    <Receipt className="w-3.5 h-3.5" />
                    Invoice {invoice.invoice_number} · Generated
                  </span>
                );
              })()}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Booking Details</h1>
            {(quote.destination || booking.destination) && <p className="text-gray-500">{quote.destination || booking.destination} {nights > 0 && `| ${nights}N / ${nights+1}D`}</p>}
          </div>
          <Button variant="outline" onClick={() => window.history.back()} className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Overview */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="trip-overview">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg"><Plane className="w-5 h-5 text-emerald-600" />Trip Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-gray-400 mt-0.5" /><div><p className="text-sm text-gray-500">Booking No</p><p className="font-semibold text-gray-800">{bookingNo}</p></div></div>
                  <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-gray-400 mt-0.5" /><div><p className="text-sm text-gray-500">Destination</p><p className="font-semibold text-gray-800">{quote.destination || booking.destination || '-'}</p></div></div>
                  <div className="flex items-start gap-3"><Users className="w-5 h-5 text-gray-400 mt-0.5" /><div><p className="text-sm text-gray-500">Travelers</p><p className="font-semibold text-gray-800">{quote.pax_adults || booking.pax_adults || 0} Adults{(quote.cwb||0) > 0 && `, ${quote.cwb} CWB`}{(quote.cnb||0) > 0 && `, ${quote.cnb} CNB`}{(quote.pax_infants||0) > 0 && `, ${quote.pax_infants} Infants`}</p>
                    {(quote.cwb_ages || quote.cnb_ages) && <p className="text-xs text-gray-400">{quote.cwb_ages && `CWB Ages: ${quote.cwb_ages} yrs`}{quote.cwb_ages && quote.cnb_ages && ' | '}{quote.cnb_ages && `CNB Ages: ${quote.cnb_ages} yrs`}</p>}
                  </div></div>
                  <div className="flex items-start gap-3"><Calendar className="w-5 h-5 text-gray-400 mt-0.5" /><div><p className="text-sm text-gray-500">Travel Date</p><p className="font-semibold text-gray-800">{formatDate(travelDate)}</p></div></div>
                  <div className="flex items-start gap-3"><Calendar className="w-5 h-5 text-gray-400 mt-0.5" /><div><p className="text-sm text-gray-500">Return Date</p><p className="font-semibold text-gray-800">{formatDate(returnDate)}</p></div></div>
                  {nights > 0 && <div className="flex items-start gap-3"><Moon className="w-5 h-5 text-gray-400 mt-0.5" /><div><p className="text-sm text-gray-500">Duration</p><p className="font-semibold text-gray-800">{nights}N / {nights+1}D</p></div></div>}
                </div>
              </CardContent>
            </Card>

            {/* Guest Information (Editable) */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="guest-info-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg"><User className="w-5 h-5 text-blue-600" />Guest Information</CardTitle>
                  {!editingGuest && (
                    <Button variant="ghost" size="sm" onClick={() => setEditingGuest(true)} data-testid="edit-guest-btn">
                      <Edit3 className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  )}
                  {editingGuest && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveGuestInfo} disabled={savingGuest} data-testid="save-guest-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <Save className="w-4 h-4 mr-1" /> {savingGuest ? 'Saving...' : 'Save'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEditingGuest(false); setGuestForm({ guest_name: booking.guest_name || quote.client_name || '', guest_mobile: booking.guest_mobile || booking.client_phone || quote.client_phone || '', guest_email: booking.guest_email || booking.client_email || quote.client_email || '' }); }} data-testid="cancel-guest-btn">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editingGuest ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500 mb-1 block">Guest Name</label>
                      <Input value={guestForm.guest_name} onChange={e => setGuestForm(p => ({...p, guest_name: e.target.value}))} data-testid="guest-name-input" />
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 mb-1 block">Guest Mobile</label>
                      <Input value={guestForm.guest_mobile} onChange={e => setGuestForm(p => ({...p, guest_mobile: e.target.value}))} data-testid="guest-mobile-input" />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3"><User className="w-5 h-5 text-gray-400" /><div><p className="text-sm text-gray-500">Guest Name</p><p className="font-semibold text-gray-800">{guestForm.guest_name || '-'}</p></div></div>
                    <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-gray-400" /><div><p className="text-sm text-gray-500">Guest Mobile</p>{guestForm.guest_mobile ? <a href={`tel:${guestForm.guest_mobile}`} className="font-semibold text-emerald-600 hover:underline">{guestForm.guest_mobile}</a> : <p className="text-gray-400">-</p>}</div></div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Flight Details */}
            {(flightDetails.length > 0 || !isAdmin) && (
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="flight-details">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg"><Plane className="w-5 h-5 text-sky-600" />Flight Details</CardTitle>
                    <Button variant="ghost" size="sm" onClick={openFlightEditor} data-testid="edit-flight-btn" className="text-sky-700 hover:text-sky-800 hover:bg-sky-50">
                      <Edit3 className="w-4 h-4 mr-1" /> {flightDetails.length > 0 ? 'Edit' : 'Add Flights'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {flightDetails.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">No flight details added yet. Click <span className="font-semibold text-sky-700">Add Flights</span> to enter them.</p>
                  )}
                  {flightDetails.map((fl, idx) => (
                    <div key={idx} className="bg-gradient-to-r from-sky-50 to-white rounded-xl p-4 border border-sky-100">
                      {(fl.airline || fl.flight_number) && <p className="text-sm font-semibold text-sky-700 mb-3">{fl.airline} {fl.flight_number && `| ${fl.flight_number}`}</p>}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 text-center">
                          <p className="text-xl font-bold text-gray-900">{fl.from || fl.departure_city || '-'}</p>
                          <p className="text-sm text-gray-800 mt-1"><span className="font-semibold">{fl.departure_code || ''}</span>{(fl.time || fl.departure_time) && <span className="ml-2 text-lg font-bold">{fl.time || fl.departure_time}</span>}</p>
                          <p className="text-xs text-gray-400 mt-1">{fl.date || fl.departure_date || ''}</p>
                        </div>
                        <div className="flex-shrink-0 px-4 text-center">
                          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-sky-400" /><div className="w-16 h-0.5 bg-gradient-to-r from-sky-400 to-emerald-400" /><Plane className="w-4 h-4 text-emerald-500 rotate-90" /><div className="w-16 h-0.5 bg-gradient-to-r from-emerald-400 to-emerald-400" /><div className="w-2 h-2 rounded-full bg-emerald-400" /></div>
                          {fl.pnr && <p className="text-xs text-gray-500 mt-1">PNR: <span className="font-semibold text-gray-700">{fl.pnr}</span></p>}
                          {fl.duration && <p className="text-xs text-gray-500 mt-1">{fl.duration}</p>}
                          <p className="text-xs text-gray-400">{fl.connection_type === 'non_stop' ? 'Non Stop' : (fl.stops ? `${fl.stops} Stop` : '')}</p>
                        </div>
                        <div className="flex-1 text-center">
                          <p className="text-xl font-bold text-gray-900">{fl.to || fl.arrival_city || '-'}</p>
                          <p className="text-sm text-gray-800 mt-1">{fl.arrival_time && <span className="text-lg font-bold mr-2">{fl.arrival_time}</span>}<span className="font-semibold">{fl.arrival_code || ''}</span></p>
                          <p className="text-xs text-gray-400 mt-1">{fl.arrival_date || ''}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Hotel Details with Confirmation Numbers */}
            {(hotels.length > 0 || canAgentEditHotels) && (
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="hotel-details">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg"><Hotel className="w-5 h-5 text-purple-600" />Hotel Details</CardTitle>
                    <div className="flex items-center gap-2">
                      {(canAgentEditHotels || isAdmin) && !editingConf && (
                        <Button variant="ghost" size="sm" onClick={openHotelEditor} data-testid="edit-hotel-btn" className="text-purple-700 hover:text-purple-800 hover:bg-purple-50">
                          <Edit3 className="w-4 h-4 mr-1" /> Edit Hotels
                        </Button>
                      )}
                      {isAdmin && !editingConf && (
                        <Button variant="ghost" size="sm" onClick={() => {
                          setEditingConf(true);
                          setConfForm(hotels.map((h, idx) => {
                            const hotelName = h.hotel_name || h.name || '';
                            // Positional match takes priority so duplicate hotel names keep their own conf #.
                            const positional = confirmations[idx]?.confirmation_number || '';
                            const existing = positional || legacyConfMap[hotelName] || '';
                            return { hotel: hotelName, city: h.city || '', confirmation_number: existing };
                          }));
                        }} data-testid="edit-conf-btn">
                          <Edit3 className="w-4 h-4 mr-1" /> Edit Confirmations
                        </Button>
                      )}
                      {editingConf && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveConfirmations} disabled={savingConf} data-testid="save-conf-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                            <Save className="w-4 h-4 mr-1" /> {savingConf ? 'Saving...' : 'Save'}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditingConf(false)} data-testid="cancel-conf-btn">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {hotels.length === 0 && canAgentEditHotels && (
                    <p className="text-sm text-gray-500 text-center py-4">No hotels added yet. Click <span className="font-semibold text-purple-700">Edit Hotels</span> to enter accommodation details.</p>
                  )}
                  {hotels.map((hotel, idx) => {
                    const hotelName = hotel.hotel_name || hotel.name || '-';
                    // Positional confirmation: hotels[idx] ↔ confirmations[idx]. Keeps duplicate-hotel-name rows independent.
                    // Legacy fallback (name/city) only used when the positional slot is empty.
                    const positionalConf = confirmations[idx]?.confirmation_number || '';
                    const confNum = positionalConf || legacyConfMap[hotelName] || (confirmations.find(c => (c.city||'').toLowerCase() === (hotel.city||'').toLowerCase())?.confirmation_number) || '';
                    return (
                      <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-gray-100 border-l-4 border-l-purple-500">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-bold text-gray-800 text-lg">{hotelName}</p>
                            <p className="text-sm text-gray-500">{hotel.city || quote.destination || ''}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {(hotel.hotel_category || quote.hotel_category) && <Badge className="bg-purple-100 text-purple-700">{hotel.hotel_category || quote.hotel_category}</Badge>}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div className="min-w-0"><p className="text-gray-500">No of Rooms</p><p className="font-medium text-gray-800 break-words">{hotel.rooms_count || hotel.rooms || 1}</p></div>
                          <div className="min-w-0"><p className="text-gray-500">Room Category</p><p className="font-medium text-gray-800 break-words">{hotel.room_type || hotel.room_category || 'Standard'}</p></div>
                          <div className="min-w-0"><p className="text-gray-500">Meal Plan</p><p className="font-medium text-gray-800 break-words">{(hotel.meal_plan || quote.meal_plan || '-').toUpperCase()}</p></div>
                          <div className="min-w-0"><p className="text-gray-500">Nights</p><p className="font-medium text-gray-800">{hotel.nights || 1}</p></div>
                          <div className="min-w-0"><p className="text-gray-500">Check In</p><p className="font-medium text-gray-800">{formatDate(hotel.check_in)}</p></div>
                          <div className="min-w-0"><p className="text-gray-500">Check Out</p><p className="font-medium text-gray-800">{formatDate(hotel.check_out)}</p></div>
                          {hotel.extra_bed_toggle && hotel.extra_bed_count > 0 && <div className="min-w-0"><p className="text-gray-500">EB / EM</p><p className="font-medium text-gray-800">{hotel.extra_bed_count}</p></div>}
                          {hotel.cwb_toggle && hotel.cwb_count > 0 && <div className="min-w-0"><p className="text-gray-500">CWB</p><p className="font-medium text-gray-800">{hotel.cwb_count}</p></div>}
                          {hotel.cnb_toggle && hotel.cnb_count > 0 && <div className="min-w-0"><p className="text-gray-500">CNB</p><p className="font-medium text-gray-800">{hotel.cnb_count}</p></div>}
                        </div>
                        {/* Confirmation Number - always shown */}
                        <div className="mt-3 pt-3 border-t border-gray-200" data-testid={`hotel-conf-${idx}`}>
                          {editingConf ? (
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-emerald-700 whitespace-nowrap">Confirmation No:</span>
                              <Input
                                value={confForm[idx]?.confirmation_number || ''}
                                onChange={e => {
                                  const updated = [...confForm];
                                  updated[idx] = { ...updated[idx], confirmation_number: e.target.value };
                                  setConfForm(updated);
                                }}
                                placeholder="Enter confirmation number"
                                className="h-8 text-sm max-w-xs"
                                data-testid={`conf-input-${idx}`}
                              />
                            </div>
                          ) : (
                            <p className="text-sm font-semibold text-emerald-700">
                              Confirmation No: <span className="font-mono bg-emerald-50 px-2 py-0.5 rounded">{confNum || <span className="text-gray-400 font-normal italic">Not yet assigned</span>}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Day-wise Itinerary - Redesigned */}
            {itinerary.length > 0 && (
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="itinerary">
                <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-lg"><ListOrdered className="w-5 h-5 text-indigo-600" />Day-wise Itinerary</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-5">
                    {itinerary.map((day, idx) => {
                      const dayDate = parseDayDate(day.date, travelDate, idx);
                      const dateLabel = formatDayDateLabel(dayDate);
                      const dayCity = (day.city || day.title || '').toUpperCase();
                      const activities = (day.activities || []).filter(a => (a.name || '').trim());
                      const fallbackActivity = activities.length === 0 ? (day.sightseeing || '') : '';
                      const headlineDesc = activities.length === 0 ? (day.description || '') : '';
                      const vehicle = day.vehicle || '';
                      const hotelInfo = day.hotel || '';
                      const dayHotel = hotelByCity[(day.city || '').toLowerCase()];
                      const accommodation = hotelInfo || (dayHotel ? `${dayHotel.hotel_name || dayHotel.name}${dayHotel.hotel_category ? ` (${dayHotel.hotel_category})` : ''}` : '');
                      const xfer = (day.transfer_type || '').toLowerCase();
                      const isNoTransfer = xfer === 'none' || xfer === 'no_transfer' || xfer === 'no-transfer';
                      const transferLabel = isNoTransfer ? null
                        : (xfer === 'sic' || xfer === 'seat_in_coach' || xfer === 'seat-in-coach') ? 'Seat in Coach (SIC)' : 'Private';
                      const isLastDay = idx === itinerary.length - 1;

                      return (
                        <div key={idx} className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden" data-testid={`itinerary-day-${idx}`}>
                          <div className="p-5 pb-4">
                            <h4 className="text-lg font-bold text-gray-900 tracking-wide">
                              DAY {day.day || idx + 1}: {dateLabel}{dayCity && !isLastDay ? ` - ${dayCity}` : ''}
                            </h4>
                            {fallbackActivity && (
                              <p className="mt-4 font-bold text-gray-800 text-base uppercase">{fallbackActivity}</p>
                            )}
                            {headlineDesc && headlineDesc !== `Sightseeing: ${fallbackActivity}` && (
                              <p className="mt-2 text-sm text-gray-500 leading-relaxed whitespace-pre-line">{headlineDesc}</p>
                            )}
                            {activities.length > 0 && (
                              <div className="mt-4 space-y-3" data-testid={`itinerary-day-${idx}-activities`}>
                                {activities.map((act, ai) => {
                                  const type = (act.type || '').toLowerCase();
                                  const isEntry = type === 'entry_ticket';
                                  return (
                                    <div key={ai} className="flex items-start gap-2">
                                      {isEntry && (
                                        <span className="mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 uppercase tracking-wide shrink-0">
                                          Entry Ticket
                                        </span>
                                      )}
                                      <div className="flex-1">
                                        <p className="font-bold text-gray-800 text-sm uppercase">{act.name}</p>
                                        {act.description && act.description !== `Sightseeing: ${act.name}` && (
                                          <p className="mt-1 text-sm text-gray-500 leading-relaxed whitespace-pre-line">{act.description}</p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          {/* Bottom info row — Accommodation, Transfer, Vehicle (in that order) */}
                          <div className="mx-5 border-t border-gray-200" />
                          <div className="px-5 py-4 flex flex-wrap gap-6">
                            {accommodation && (
                              <div className="flex items-center gap-3" data-testid={`itinerary-day-${idx}-accommodation`}>
                                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                                  <Hotel className="w-5 h-5 text-indigo-500" />
                                </div>
                                <div>
                                  <p className="text-xs text-gray-400 uppercase tracking-wider">Accommodation</p>
                                  <p className="font-semibold text-gray-800 text-sm">{accommodation}</p>
                                </div>
                              </div>
                            )}
                            {transferLabel && (
                              <div className="flex items-center gap-3" data-testid={`itinerary-day-${idx}-transfer`}>
                                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                                  <Car className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                  <p className="text-xs text-gray-400 uppercase tracking-wider">Transfer</p>
                                  <p className="font-semibold text-gray-800 text-sm">{transferLabel}</p>
                                </div>
                              </div>
                            )}
                            {vehicle && (
                              <div className="flex items-center gap-3" data-testid={`itinerary-day-${idx}-vehicle`}>
                                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                                  <Car className="w-5 h-5 text-orange-500" />
                                </div>
                                <div>
                                  <p className="text-xs text-gray-400 uppercase tracking-wider">Vehicle</p>
                                  <p className="font-semibold text-gray-800 text-sm">{vehicle}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Inclusions & Exclusions */}
            {(quote.inclusions || quote.exclusions) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quote.inclusions && (
                  <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-emerald-500" data-testid="inclusions">
                    <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-lg text-emerald-600"><CheckCircle className="w-5 h-5" />Inclusions</CardTitle></CardHeader>
                    <CardContent>
                      <ul className="space-y-1.5">{quote.inclusions.split('\n').filter(l => l.trim()).map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm"><span className="text-emerald-400 mt-0.5 flex-shrink-0">&#8226;</span><span className="text-gray-700">{item.replace(/^[-•*]\s*/,'')}</span></li>
                      ))}</ul>
                    </CardContent>
                  </Card>
                )}
                {quote.exclusions && (
                  <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-red-500" data-testid="exclusions">
                    <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-lg text-red-600"><XCircle className="w-5 h-5" />Exclusions</CardTitle></CardHeader>
                    <CardContent>
                      <ul className="space-y-1.5">{quote.exclusions.split('\n').filter(l => l.trim()).map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm"><span className="text-red-400 mt-0.5 flex-shrink-0">&#8226;</span><span className="text-gray-700">{item.replace(/^[-•*]\s*/,'')}</span></li>
                      ))}</ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Terms & Conditions */}
            {quote.show_terms_conditions && quote.terms_conditions && (
              <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-indigo-500" data-testid="terms-conditions">
                <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-lg text-indigo-600"><ScrollText className="w-5 h-5" />Terms & Conditions</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-2">{quote.terms_conditions.split('\n').filter(l => l.trim()).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm"><span className="text-indigo-400 mt-0.5 flex-shrink-0">&#8226;</span><span className="text-gray-700">{item.replace(/^[-•*]\s*/,'')}</span></li>
                  ))}</ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Payment Summary */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="payment-summary-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 text-lg"><CreditCard className="w-5 h-5 text-emerald-600" />Payment Summary</CardTitle>
                  {booking.status !== 'confirmed' && (
                    <button
                      role="switch"
                      aria-checked={!!booking.gst_included}
                      onClick={async () => {
                        try {
                          const next = !booking.gst_included;
                          await axios.patch(
                            `${API}/bookings/${id}/gst-toggle?enabled=${next}`,
                            {},
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          setBooking((prev) => prev ? { ...prev, gst_included: next } : prev);
                          toast.success(next ? "GST included" : "GST hidden");
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to update GST");
                        }
                      }}
                      disabled={!isAdmin && !!booking.status && booking.status === 'confirmed'}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                        booking.gst_included ? 'bg-emerald-600' : 'bg-gray-200'
                      } disabled:opacity-50`}
                      title={booking.gst_included ? "GST included — click to exclude" : "Click to include GST"}
                      data-testid="payment-gst-toggle"
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                          booking.gst_included ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  )}
                </div>
                {booking.status !== 'confirmed' && (
                  <p className="text-[11px] text-gray-400 mt-1">GST {booking.gst_included ? 'will be charged' : 'not applicable'} · locks on booking confirmation</p>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  const dmcPrice = quote.quote_amount || booking.total_amount || 0;
                  const gstRate = typeof booking.gst_rate === 'number' ? booking.gst_rate : 0.05;
                  const showGst = !!booking.gst_included;
                  const gstAmount = showGst ? Math.round(dmcPrice * gstRate) : 0;
                  const effectiveTotal = dmcPrice + gstAmount;
                  // Auto-sum from Payment Detail entries instead of relying on stale booking.amount_paid
                  const sumReceived = (payments || []).reduce((s, p) => s + (Number(p.amount_received) || 0), 0);
                  const amountPaid = sumReceived > 0 ? sumReceived : (booking.amount_paid || 0);
                  const balanceDue = Math.max(0, effectiveTotal - amountPaid);
                  return (
                    <>
                      <div className="flex justify-between items-center"><span className="text-gray-500">Booking Amount</span><span className="text-xl font-bold text-gray-900" data-testid="booking-amount">{formatCurrency(dmcPrice)}</span></div>
                      {showGst && (
                        <>
                          <div className="flex justify-between items-center text-sm"><span className="text-gray-500">GST @ {Math.round(gstRate * 100)}%</span><span className="font-medium text-gray-700">{formatCurrency(gstAmount)}</span></div>
                          <div className="flex justify-between items-center text-sm bg-emerald-50 -mx-6 px-6 py-2 rounded" data-testid="booking-total-with-gst"><span className="text-emerald-700 font-semibold">Total with GST</span><span className="font-bold text-emerald-700">{formatCurrency(effectiveTotal)}</span></div>
                          {quote.gstin && <div className="flex justify-between items-center text-sm"><span className="text-gray-500">GSTIN</span><span className="font-mono text-gray-700 text-xs">{quote.gstin}</span></div>}
                        </>
                      )}
                      <div className="flex justify-between items-center"><span className="text-gray-500">Received Amount</span><span className="text-lg font-semibold text-emerald-600" data-testid="received-amount">{formatCurrency(amountPaid)}</span></div>
                      <div className="flex justify-between items-center pt-3 border-t border-gray-100"><span className="text-gray-500">Balance Amount</span><span className="text-lg font-semibold text-amber-600" data-testid="balance-amount">{balanceDue > 0 ? formatCurrency(balanceDue) : 'Nil'}</span></div>
                      <div className="pt-2">{getPaymentBadge(booking.payment_status)}</div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Payment Detail — visible to both admin & agent */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="payment-detail-card">
              <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-lg"><Banknote className="w-5 h-5 text-green-600" />Payment Detail</CardTitle></CardHeader>
              <CardContent>
                {payments.length > 0 ? (
                  <div className="space-y-3">
                    {payments.map((p, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-testid={`payment-item-${idx}`}>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm text-gray-500">Payment Received Date</p>
                            <p className="font-semibold text-gray-800">{formatDate(p.payment_date)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">Amount Received</p>
                            <p className="font-bold text-emerald-700 text-lg">{formatCurrency(p.amount_received)}</p>
                          </div>
                        </div>
                        {p.payment_mode && <p className="text-xs text-gray-400 mt-1 capitalize">Mode: {p.payment_mode.replace('_', ' ')}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic" data-testid="no-payments">No payments recorded yet</p>
                )}
              </CardContent>
            </Card>

            {/* My Profit Snapshot — Agent only (mirrors Admin pattern) */}
            {!isAdmin && agentMarkup && (
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="agent-markup-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <PiggyBank className="w-5 h-5 text-emerald-600" />
                    My Profit Snapshot
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2.5">
                    {/* Booking Amount = Agent Quote (DMC + Markup) */}
                    <div className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2 border border-amber-100" data-testid="agent-booking-amt">
                      <span className="text-xs text-amber-700 uppercase tracking-wide font-semibold">Booking Amount</span>
                      <span className="text-base font-bold text-amber-800">{formatCurrency(agentMarkup.agent_quote_amount)}</span>
                    </div>
                    {/* DMC Cost — what the agent pays the DMC */}
                    <div className="flex items-center justify-between bg-orange-50 rounded-lg px-3 py-2 border border-orange-100" data-testid="agent-dmc-cost">
                      <span className="text-xs text-orange-700 uppercase tracking-wide font-semibold">DMC Cost</span>
                      <span className="text-base font-bold text-orange-800">{formatCurrency(agentMarkup.dmc_base)}</span>
                    </div>
                    {/* Net Profit + Margin */}
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-lg p-4 text-white shadow-md" data-testid="agent-markup-profit">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-emerald-100 text-xs uppercase tracking-wide font-semibold">Net Profit</p>
                          <p className="text-2xl font-bold mt-0.5">{formatCurrency(agentMarkup.agent_markup)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-emerald-100 text-xs uppercase tracking-wide font-semibold">Margin</p>
                          <p className="text-2xl font-bold mt-0.5">{agentMarkup.markup_margin}%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Editable Markup — Agent only. Persists to linked quote so all profit math stays consistent. */}
            {!isAdmin && (Number(quote.quote_per_adult || 0) > 0 || Number(quote.quote_amount || 0) > 0 || Number(booking.total_amount || 0) > 0) && (
              <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 shadow-sm" data-testid="agent-markup-edit-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg text-purple-800">
                      <Calculator className="w-5 h-5" /> My Markup
                    </CardTitle>
                    <div className="flex items-center gap-1 bg-white/70 rounded-lg p-1" data-testid="markup-mode-toggle">
                      <button
                        onClick={() => setMarkupForm(f => ({ ...f, mode: 'manual' }))}
                        data-testid="markup-manual-btn"
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${markupForm.mode === 'manual' ? 'bg-purple-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                      >Manual</button>
                      <button
                        onClick={() => setMarkupForm(f => ({ ...f, mode: 'percentage' }))}
                        data-testid="markup-percentage-btn"
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1 ${markupForm.mode === 'percentage' ? 'bg-purple-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                      ><Percent className="w-3 h-3" /> %</button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {markupForm.mode === 'percentage' ? (
                    <div className="flex items-center gap-3">
                      <Label className="text-gray-600 whitespace-nowrap text-sm">Markup %</Label>
                      <Input
                        type="number"
                        value={markupForm.percentage || ''}
                        onChange={e => setMarkupForm(f => ({ ...f, percentage: parseFloat(e.target.value) || 0 }))}
                        className="w-24 bg-white border-purple-200"
                        placeholder="0"
                        data-testid="markup-percentage-input"
                      />
                      <span className="text-gray-500 text-sm">%</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-gray-600 text-sm">Per Adult</Label>
                        <Input
                          type="number"
                          value={markupForm.adult || ''}
                          onChange={e => setMarkupForm(f => ({ ...f, adult: parseFloat(e.target.value) || 0 }))}
                          className="bg-white border-gray-200 mt-1"
                          placeholder="0"
                          data-testid="markup-adult-input"
                        />
                      </div>
                      {(Number(quote.quote_per_cwb || 0) > 0 || Number(quote.cwb || 0) > 0 || Number(quote.cwb_count || 0) > 0) && (
                        <div>
                          <Label className="text-blue-600 text-sm">Per CWB</Label>
                          <Input
                            type="number"
                            value={markupForm.cwb || ''}
                            onChange={e => setMarkupForm(f => ({ ...f, cwb: parseFloat(e.target.value) || 0 }))}
                            className="bg-blue-50 border-blue-200 mt-1"
                            placeholder="0"
                            data-testid="markup-cwb-input"
                          />
                        </div>
                      )}
                      {(Number(quote.quote_per_cnb || 0) > 0 || Number(quote.cnb || 0) > 0 || Number(quote.cnb_count || 0) > 0) && (
                        <div>
                          <Label className="text-amber-600 text-sm">Per CNB</Label>
                          <Input
                            type="number"
                            value={markupForm.cnb || ''}
                            onChange={e => setMarkupForm(f => ({ ...f, cnb: parseFloat(e.target.value) || 0 }))}
                            className="bg-amber-50 border-amber-200 mt-1"
                            placeholder="0"
                            data-testid="markup-cnb-input"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selling Price Breakdown (live preview) */}
                  <div className="pt-3 border-t border-purple-200">
                    <p className="text-xs font-semibold text-purple-700 mb-2 uppercase tracking-wide">Selling Price (preview)</p>
                    <div className="bg-white/80 rounded-lg p-3 space-y-2">
                      {(() => {
                        const pctMode = markupForm.mode === 'percentage';
                        const pa = Number(quote.quote_per_adult || 0);
                        const pcw = Number(quote.quote_per_cwb || 0);
                        const pcn = Number(quote.quote_per_cnb || 0);
                        const adultMk = pctMode ? pa * (Number(markupForm.percentage || 0) / 100) : Number(markupForm.adult || 0);
                        const cwbMk = pctMode ? pcw * (Number(markupForm.percentage || 0) / 100) : Number(markupForm.cwb || 0);
                        const cnbMk = pctMode ? pcn * (Number(markupForm.percentage || 0) / 100) : Number(markupForm.cnb || 0);
                        const adults = Number(quote.pax_adults || 0);
                        const cwbN = Number(quote.cwb_count || quote.cwb || 0);
                        const cnbN = Number(quote.cnb_count || quote.cnb || 0);
                        const total = (pa + adultMk) * adults + (pcw + cwbMk) * cwbN + (pcn + cnbMk) * cnbN;
                        return (
                          <>
                            <div className="flex justify-between text-sm" data-testid="selling-price-adult">
                              <span className="text-gray-600">Per Adult</span>
                              <span className="font-medium text-gray-800">{formatCurrency(pa + adultMk)}</span>
                            </div>
                            {cwbN > 0 && (
                              <div className="flex justify-between text-sm" data-testid="selling-price-cwb">
                                <span className="text-blue-600">Per CWB</span>
                                <span className="font-medium text-gray-800">{formatCurrency(pcw + cwbMk)}</span>
                              </div>
                            )}
                            {cnbN > 0 && (
                              <div className="flex justify-between text-sm" data-testid="selling-price-cnb">
                                <span className="text-amber-600">Per CNB</span>
                                <span className="font-medium text-gray-800">{formatCurrency(pcn + cnbMk)}</span>
                              </div>
                            )}
                            <div className="flex justify-between pt-2 border-t border-gray-200 text-sm font-semibold" data-testid="selling-price-total">
                              <span className="text-gray-700">Total Selling Price</span>
                              <span className="text-purple-700">{formatCurrency(total)}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <Button
                    onClick={saveAgentMarkup}
                    disabled={savingMarkup}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="save-markup-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingMarkup ? 'Saving...' : 'Save Markup'}
                  </Button>
                </CardContent>
              </Card>
            )}


            {isAdmin && vendorSummaries && vendorSummaries.length > 0 && (
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="vendor-detail-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="w-5 h-5 text-slate-600" />
                    Vendor Detail
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-gray-500 px-1">
                      <div className="col-span-6">Vendor Name</div>
                      <div className="col-span-3 text-right">Paid</div>
                      <div className="col-span-3 text-right">Balance</div>
                    </div>
                    {vendorSummaries.map((v, i) => (
                      <div
                        key={`${v.vendor_name}-${i}`}
                        className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-lg px-3 py-2 border border-slate-100"
                        data-testid={`vendor-row-${i}`}
                      >
                        <div className="col-span-6 text-sm text-slate-800 font-medium truncate" title={v.vendor_name}>{v.vendor_name}</div>
                        <div className="col-span-3 text-right text-sm font-semibold text-orange-700 tabular-nums">{formatCurrency(v.advance_paid)}</div>
                        <div className={`col-span-3 text-right text-sm font-semibold tabular-nums ${Number(v.balance_amount || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {Number(v.balance_amount || 0) > 0 ? formatCurrency(v.balance_amount) : 'Nil'}
                        </div>
                      </div>
                    ))}
                    <div
                      className="flex items-center justify-between pt-3 mt-2 border-t border-slate-200"
                      data-testid="vendor-total-paid"
                    >
                      <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Total Amount Paid</span>
                      <span className="text-base font-bold text-slate-900 tabular-nums">
                        {formatCurrency(vendorSummaries.reduce((s, v) => s + Number(v.advance_paid || 0), 0))}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {isAdmin && profitData && (
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="profit-snapshot-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <PiggyBank className="w-5 h-5 text-emerald-600" />
                    Profit Snapshot
                    {!invoice && (
                      <span className="text-[10px] font-normal text-gray-400 ml-auto">GST applies after invoice</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    // Backend `/profit` endpoint already applies the correct math:
                    //   • Non-GST agent: Profit = (Booking − Vendor Paid) / 1.18,  GST = Profit × 18%
                    //   • GST agent:     GST = invoice.tax_amount (5%), Profit = Booking − Vendor − GST
                    // We just render those values verbatim.
                    const hasInvoice = !!invoice;
                    const gstAmount = profitData.gst_amount || 0;
                    const netProfit = Math.round(profitData.net_profit || 0);
                    const margin = profitData.profit_margin != null
                      ? profitData.profit_margin
                      : ((profitData.booking_amount || 0) > 0
                          ? Math.round(netProfit / profitData.booking_amount * 1000) / 10
                          : 0);
                    return (
                      <div className="space-y-2.5">
                        {/* Line 1 — Booking Amount */}
                        <div className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2 border border-amber-100" data-testid="profit-booking-amt">
                          <span className="text-xs text-amber-700 uppercase tracking-wide font-semibold">Booking Amount</span>
                          <span className="text-base font-bold text-amber-800">{formatCurrency(profitData.booking_amount)}</span>
                        </div>
                        {/* Line 2 — Vendor Paid */}
                        <div className="flex items-center justify-between bg-orange-50 rounded-lg px-3 py-2 border border-orange-100" data-testid="profit-vendor-paid">
                          <span className="text-xs text-orange-700 uppercase tracking-wide font-semibold">Vendor Paid</span>
                          <span className="text-base font-bold text-orange-800">{formatCurrency(profitData.vendor_paid)}</span>
                        </div>
                        {/* Line 3 — GST (only when invoice is generated) */}
                        {hasInvoice && (
                          <div className="flex items-center justify-between bg-violet-50 rounded-lg px-3 py-2 border border-violet-100" data-testid="profit-gst">
                            <span className="text-xs text-violet-700 uppercase tracking-wide font-semibold">GST ({Math.round(profitData.gst_rate || 18)}%)</span>
                            <span className="text-base font-bold text-violet-800">{formatCurrency(gstAmount)}</span>
                          </div>
                        )}
                        {/* Net Profit + Margin on same line */}
                        <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-lg p-4 text-white shadow-md" data-testid="profit-net">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-emerald-100 text-xs uppercase tracking-wide font-semibold">Net Profit</p>
                              <p className="text-2xl font-bold mt-0.5">{formatCurrency(netProfit)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-emerald-100 text-xs uppercase tracking-wide font-semibold">Margin</p>
                              <p className="text-2xl font-bold mt-0.5">{margin}%</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}


            {/* Invoice Section - Admin always sees; Agent only sees if sent_to_agent */}
            {(isAdmin || (invoice && invoice.sent_to_agent)) && (
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="invoice-section">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg"><Receipt className="w-5 h-5 text-violet-600" />Invoice</CardTitle>
                  {isAdmin && invoice && (
                    <Button variant="ghost" size="sm" onClick={() => setShowInvoiceEditor(true)} data-testid="edit-invoice-btn">
                      <Edit3 className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {invoice ? (
                  <>
                    <div className="bg-violet-50 rounded-lg p-3 border border-violet-100 space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Invoice #</span>
                        <span className="font-mono font-semibold text-violet-700" data-testid="invoice-number">{invoice.invoice_number}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Date</span>
                        <span className="text-gray-800">{invoice.invoice_date}</span>
                      </div>
                      {isAdmin && (invoice.hsn_code || invoice.tax_rate != null) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">HSN/SAC</span>
                          <span className="text-gray-800">
                            {(() => {
                              const stored = String(invoice.hsn_code || '').trim();
                              if (stored.length >= 6) return stored;
                              return Number(invoice.tax_rate) === 18 ? '998555' : '998552';
                            })()}
                          </span>
                        </div>
                      )}
                      {isAdmin && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">GST</span>
                          <span className="text-gray-800">
                            {invoice.gst_type === 'igst' ? `IGST @ ${invoice.tax_rate}%` : `CGST ${invoice.tax_rate/2}% + SGST ${invoice.tax_rate/2}%`}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm pt-1 border-t border-violet-200">
                        <span className="text-gray-500">Total</span>
                        <span className="font-bold text-gray-900">{formatCurrency(invoice.total_amount || invoice.grand_total)}</span>
                      </div>
                      {isAdmin && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Balance</span>
                          <span className={`font-semibold ${invoice.balance_due > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatCurrency(invoice.balance_due)}</span>
                        </div>
                      )}
                      {invoice.total_in_words && (
                        <p className="text-xs text-gray-500 italic pt-1 border-t border-violet-200" data-testid="total-in-words">{invoice.total_in_words}</p>
                      )}
                    </div>
                    <Button
                      className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                      onClick={handleDownloadInvoice}
                      disabled={invoiceLoading}
                      data-testid="download-invoice-btn"
                    >
                      <FileDown className="w-4 h-4 mr-2" />{invoiceLoading ? 'Downloading...' : 'Download Invoice PDF'}
                    </Button>
                    {isAdmin && !invoice.sent_to_agent && (
                      <Button
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={handleSendInvoiceToAgent}
                        disabled={sendingInvoice}
                        data-testid="send-invoice-to-agent-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />{sendingInvoice ? 'Sending...' : 'Send Invoice to Agent'}
                      </Button>
                    )}
                    {isAdmin && invoice.sent_to_agent && (
                      <div className="flex items-center justify-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded-lg py-2" data-testid="invoice-sent-status">
                        <CheckCircle className="w-4 h-4" />
                        <span>Invoice sent to agent</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {isAdmin && (() => {
                      const rd = booking.return_date || quote.return_date;
                      const tourCompleted = rd && new Date(rd) < new Date();
                      return tourCompleted ? (
                        <Button
                          className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                          onClick={handleGenerateInvoice}
                          disabled={generatingInvoice}
                          data-testid="generate-invoice-btn"
                        >
                          <Receipt className="w-4 h-4 mr-2" />{generatingInvoice ? 'Generating...' : 'Generate Invoice'}
                        </Button>
                      ) : (
                        <p className="text-sm text-gray-400 italic text-center" data-testid="invoice-pending">Invoice will be auto-generated after tour completion</p>
                      );
                    })()}
                  </>
                )}
              </CardContent>
            </Card>
            )}

            {/* Invoice Editor Modal */}
            {showInvoiceEditor && invoice && (
              <InvoiceEditor
                bookingId={id}
                invoice={invoice}
                token={token}
                onSave={(updated) => { setInvoice(updated); setShowInvoiceEditor(false); }}
                onClose={() => setShowInvoiceEditor(false)}
              />
            )}


            {/* Agent/User Details */}
            {(booking.agent_details || booking.agent_name) && (
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-lg"><User className="w-5 h-5 text-blue-600" />User Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3"><User className="w-5 h-5 text-gray-400 mt-0.5" /><div><p className="text-sm text-gray-500">User Name</p><p className="font-semibold text-gray-800">{booking.agent_details?.name || booking.agent_name}</p></div></div>
                  {booking.agent_details?.designation && <div className="flex items-start gap-3"><Briefcase className="w-5 h-5 text-gray-400 mt-0.5" /><div><p className="text-sm text-gray-500">Designation</p><p className="text-gray-800">{booking.agent_details.designation}</p></div></div>}
                  {booking.agent_details?.phone && <div className="flex items-start gap-3"><Phone className="w-5 h-5 text-gray-400 mt-0.5" /><div><p className="text-sm text-gray-500">Mobile Number</p><a href={`tel:${booking.agent_details.phone}`} className="text-emerald-600 font-medium hover:underline">{booking.agent_details.phone}</a></div></div>}
                  {isAdmin && (booking.agent_details?.company_name || booking.agent_company) && <div className="flex items-start gap-3 pt-3 border-t border-gray-100"><Building2 className="w-5 h-5 text-gray-400 mt-0.5" /><div><p className="text-sm text-gray-500">Company</p><p className="text-gray-800">{booking.agent_details?.company_name || booking.agent_company}</p></div></div>}
                  {booking.agent_details?.email && <div className="flex items-start gap-3"><Mail className="w-5 h-5 text-gray-400 mt-0.5" /><div><p className="text-sm text-gray-500">Email</p><a href={`mailto:${booking.agent_details.email}`} className="text-blue-600 hover:underline text-sm break-all">{booking.agent_details.email}</a></div></div>}
                </CardContent>
              </Card>
            )}

            {/* Quotation Information */}
            {quote.id && (
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="quotation-info-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Info className="w-5 h-5 text-blue-600" />Quotation Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Quotation No.</span>
                    <span className="font-mono font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded" data-testid="quotation-number">{quote.quote_number || quote.id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  {booking.created_at && (
                    <div className="text-sm">
                      <span className="text-gray-500">Booking Confirmed</span>
                      <p className="font-medium text-gray-800 mt-0.5" data-testid="booking-confirmed-datetime">
                        {(() => {
                          const d = new Date(booking.created_at);
                          const dd = String(d.getDate()).padStart(2, '0');
                          const mm = String(d.getMonth() + 1).padStart(2, '0');
                          const yr = d.getFullYear();
                          const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                          return `${dd}/${mm}/${yr} at ${time}`;
                        })()}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Follow-up Log — Admin only */}
            {isAdmin && (
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="followup-log-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <History className="w-5 h-5 text-blue-600" />
                    Follow-up Log
                    {booking.last_followup_at && (
                      <span className="text-xs font-normal text-gray-500 ml-1">
                        Last: {(() => { const d = new Date(booking.last_followup_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })()}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add a note (optional)…"
                      value={followupNote}
                      onChange={(e) => setFollowupNote(e.target.value)}
                      className="flex-1 text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                      data-testid="followup-note-input"
                    />
                    <Button size="sm" variant="outline" disabled={savingFollowup} onClick={() => handleAddFollowup('call')} data-testid="followup-log-call-btn" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                      <Phone className="w-3.5 h-3.5 mr-1" /> Call
                    </Button>
                    <Button size="sm" variant="outline" disabled={savingFollowup} onClick={() => handleAddFollowup('note')} data-testid="followup-log-note-btn" className="border-gray-300 text-gray-700 hover:bg-gray-50">
                      <Save className="w-3.5 h-3.5 mr-1" /> Note
                    </Button>
                  </div>
                  {followups.length === 0 ? (
                    <p className="text-sm text-gray-400 italic" data-testid="no-followups">No follow-ups recorded yet</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {followups.map((f) => {
                        const typeColor = {
                          email: 'bg-blue-50 text-blue-700 border-blue-200',
                          whatsapp: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                          call: 'bg-purple-50 text-purple-700 border-purple-200',
                          note: 'bg-gray-50 text-gray-700 border-gray-200',
                        }[f.type] || 'bg-gray-50 text-gray-700 border-gray-200';
                        const ts = (() => { const d = new Date(f.created_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })();
                        return (
                          <div key={f.id} className={`rounded-lg border p-2.5 ${typeColor}`} data-testid={`followup-item-${f.id}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold uppercase tracking-wide">{f.type}</span>
                              <span className="text-xs">{ts}</span>
                            </div>
                            {f.note && <p className="text-sm mt-1 text-gray-700">{f.note}</p>}
                            <div className="text-[11px] text-gray-500 mt-1">
                              by {f.sent_by_name || 'Admin'}{f.sent_to ? ` · to ${f.sent_to}` : ''}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Agent Markup Snapshot — Agent only */}

            {/* Admin-only: Agent Download Permission Toggle */}
            {isAdmin && (
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="agent-download-toggle-card">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <FileDown className="w-4 h-4 text-emerald-600" />
                        Agent Can Download & Share PDF
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {booking.payment_status === 'full'
                          ? "Auto-enabled — payment is fully received."
                          : "Toggle ON to allow the agent to download the voucher PDF and share it on WhatsApp before full payment."}
                      </p>
                    </div>
                    <button
                      role="switch"
                      aria-checked={downloadEnabled}
                      disabled={togglingDownload}
                      onClick={() => handleToggleAgentDownload(!downloadEnabled)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                        downloadEnabled ? 'bg-emerald-600' : 'bg-gray-200'
                      } disabled:opacity-50`}
                      data-testid="agent-download-toggle"
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          downloadEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="booking-actions-card">
              <CardContent className="p-4 space-y-2">
                {/* Agent gating notice */}
                {!isAdmin && !downloadEnabled && booking.payment_status !== 'full' && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="agent-download-blocked-note">
                    Voucher download & WhatsApp share will be available once the admin enables it or after full payment.
                  </div>
                )}
                <Button
                  className="w-full justify-start bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!isAdmin && !downloadEnabled && booking.payment_status !== 'full'}
                  onClick={() => {
                    const quoteId = booking.quote_id || quote.id;
                    if (!quoteId) { toast.error("No linked quotation found"); return; }
                    const phone = quote.client_phone ? quote.client_phone.replace(/\D/g, '') : '';
                    const guestName = guestForm.guest_name || 'Guest';
                    const publicUrl = `${window.location.origin}/quote/${quoteId}`;
                    const msg = `Hi ${guestName}! Here is your booking confirmation for *${quote.destination || booking.destination || 'your trip'}* (Booking #${bookingNo}).\n\nView Details: ${publicUrl}\n\nRegards,\nHappy Holidays DMC`;
                    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
                  }}
                  data-testid="booking-share-whatsapp-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" /> Share PDF on WhatsApp
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start border-sky-300 text-sky-600 hover:bg-sky-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!isAdmin && !downloadEnabled && booking.payment_status !== 'full'}
                  onClick={async () => {
                    try {
                      const quoteId = booking.quote_id || quote?.id;
                      if (!quoteId) { toast.error("No linked quotation found"); return; }
                      // Build agent/advisor block from the logged-in user (agent → agent info; admin → DMC info).
                      const advisor = {
                        name: user?.name || '',
                        email: user?.email || '',
                        phone: user?.phone || '',
                        company_name: user?.company_name || 'Happy Holidays DMC',
                        designation: user?.designation || '',
                        address: user?.address || '',
                        city: user?.city || '',
                        state: user?.state || '',
                        website: user?.website || '',
                        logo_url: user?.logo_url || '',
                        profile_picture_url: user?.profile_picture_url || '',
                      };
                      const bno = booking.booking_number || bookingNo || (booking.id || '').slice(0, 8).toUpperCase();
                      // SAME PDF for Admin and Agent: rich itinerary-style PDF generated
                      // by `generateQuotePdfDoc`. The branding (logo, company info, advisor)
                      // comes from the logged-in user — admin gets DMC, agent gets agent info.
                      let q = quote;
                      if (!q || !q.itinerary) {
                        const res = await axios.get(`${API}/quotes/${quoteId}`, { headers: { Authorization: `Bearer ${token}` } });
                        q = res.data;
                      }
                      // CRITICAL: if admin/agent edited hotel details on the booking
                      // (`booking.hotels_override`), those edits must be reflected in the
                      // PDF. Otherwise the PDF keeps showing the ORIGINAL quote's room
                      // category / meal plan / extra-bed details, ignoring booking-level edits.
                      if (Array.isArray(booking.hotels_override) && booking.hotels_override.length > 0) {
                        q = {
                          ...q,
                          hotels: booking.hotels_override,
                          accommodation_options: (q.accommodation_options && q.accommodation_options.length > 0)
                            ? [{ ...q.accommodation_options[0], hotels: booking.hotels_override }, ...q.accommodation_options.slice(1)]
                            : [{ hotels: booking.hotels_override }],
                        };
                      }
                      // Reflect any flight overrides set on the booking too.
                      if (Array.isArray(booking.flights_override) && booking.flights_override.length > 0) {
                        q = { ...q, flights: booking.flights_override };
                      }
                      const doc = generateQuotePdfDoc(q, advisor, {
                        bookingConfirmations: booking.confirmations || [],
                        agentView: !isAdmin,
                        bookingMode: true,
                        bookingNumber: bno,
                      });
                      if (!doc) { toast.error("Could not generate PDF"); return; }
                      doc.save(`Booking_${bno}.pdf`);
                      toast.success("PDF downloaded!");
                    } catch (err) {
                      console.error(err);
                      toast.error("Failed to download PDF");
                    }
                  }}
                  data-testid="booking-download-pdf-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Flight Edit Modal */}
      {editingFlights && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="flight-edit-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><Plane className="w-5 h-5 text-sky-600" /> Edit Flight Details</h3>
              <Button variant="ghost" size="icon" onClick={() => setEditingFlights(false)} data-testid="close-flight-modal-btn"><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-5 space-y-4">
              {flightForm.map((f, i) => (
                <div key={i} className="rounded-xl border border-gray-200 p-4 bg-gray-50/40 relative">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold uppercase tracking-wide text-sky-700">Flight #{i + 1}</span>
                    {flightForm.length > 1 && (
                      <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        onClick={() => setFlightForm(flightForm.filter((_, idx) => idx !== i))}
                        data-testid={`remove-flight-${i}`}>
                        <X className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-500 mb-1 block">Airline</label><Input value={f.airline} onChange={e => { const v = [...flightForm]; v[i] = { ...v[i], airline: e.target.value }; setFlightForm(v); }} placeholder="e.g. IndiGo" data-testid={`flight-airline-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">Flight Number</label><Input value={f.flight_number} onChange={e => { const v = [...flightForm]; v[i] = { ...v[i], flight_number: e.target.value }; setFlightForm(v); }} placeholder="e.g. 6E 5121" data-testid={`flight-no-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">From</label><Input value={f.from} onChange={e => { const v = [...flightForm]; v[i] = { ...v[i], from: e.target.value }; setFlightForm(v); }} placeholder="DEL · Delhi" data-testid={`flight-from-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">To</label><Input value={f.to} onChange={e => { const v = [...flightForm]; v[i] = { ...v[i], to: e.target.value }; setFlightForm(v); }} placeholder="GOI · Goa" data-testid={`flight-to-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">Date</label><Input type="date" value={f.date} onChange={e => { const v = [...flightForm]; v[i] = { ...v[i], date: e.target.value }; setFlightForm(v); }} data-testid={`flight-date-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">Time</label><Input type="time" value={f.time} onChange={e => { const v = [...flightForm]; v[i] = { ...v[i], time: e.target.value }; setFlightForm(v); }} data-testid={`flight-time-${i}`} /></div>
                    <div className="md:col-span-2"><label className="text-xs text-gray-500 mb-1 block">PNR</label><Input value={f.pnr} onChange={e => { const v = [...flightForm]; v[i] = { ...v[i], pnr: e.target.value }; setFlightForm(v); }} placeholder="e.g. ABCD12" data-testid={`flight-pnr-${i}`} /></div>
                  </div>
                </div>
              ))}
              <Button variant="outline" className="w-full border-dashed" onClick={() => setFlightForm([...flightForm, { airline: '', flight_number: '', from: '', to: '', date: '', time: '', pnr: '' }])} data-testid="add-flight-row-btn">
                + Add another flight
              </Button>
            </div>
            <div className="p-5 border-t border-gray-100 flex justify-end gap-2 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={() => setEditingFlights(false)}>Cancel</Button>
              <Button onClick={saveFlightDetails} disabled={savingFlights} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="save-flight-btn">
                <Save className="w-4 h-4 mr-2" /> {savingFlights ? 'Saving...' : 'Save Flight Details'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hotel Edit Modal */}
      {editingHotels && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="hotel-edit-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><Hotel className="w-5 h-5 text-purple-600" /> Edit Hotel Details</h3>
              <Button variant="ghost" size="icon" onClick={() => setEditingHotels(false)} data-testid="close-hotel-modal-btn"><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-5 space-y-4">
              {hotelForm.map((h, i) => (
                <div key={i} className="rounded-xl border border-gray-200 p-4 bg-gray-50/40">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold uppercase tracking-wide text-purple-700">Hotel #{i + 1}</span>
                    {hotelForm.length > 1 && (
                      <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        onClick={() => setHotelForm(hotelForm.filter((_, idx) => idx !== i))}
                        data-testid={`remove-hotel-${i}`}>
                        <X className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-500 mb-1 block">Hotel Name</label><Input value={h.hotel_name} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], hotel_name: e.target.value }; setHotelForm(v); }} data-testid={`hotel-name-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">City</label><Input value={h.city} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], city: e.target.value }; setHotelForm(v); }} data-testid={`hotel-city-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">Hotel Category</label><Input value={h.hotel_category} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], hotel_category: e.target.value }; setHotelForm(v); }} placeholder="3-Star / 4-Star / 5-Star" data-testid={`hotel-cat-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">Room Type</label><Input value={h.room_type} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], room_type: e.target.value }; setHotelForm(v); }} placeholder="Standard / Deluxe / Suite" data-testid={`hotel-room-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">Meal Plan</label><Input value={h.meal_plan} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], meal_plan: e.target.value }; setHotelForm(v); }} placeholder="EP / CP / MAP / AP" data-testid={`hotel-meal-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">Nights</label><Input type="number" min={1} value={h.nights} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], nights: parseInt(e.target.value) || 1 }; setHotelForm(v); }} data-testid={`hotel-nights-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">Check In</label><Input type="date" value={h.check_in} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], check_in: e.target.value }; setHotelForm(v); }} data-testid={`hotel-checkin-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">Check Out</label><Input type="date" value={h.check_out} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], check_out: e.target.value }; setHotelForm(v); }} data-testid={`hotel-checkout-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">No of Rooms</label><Input type="number" min={1} value={h.rooms_count} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], rooms_count: parseInt(e.target.value) || 1 }; setHotelForm(v); }} data-testid={`hotel-rooms-${i}`} /></div>
                    <div><label className="text-xs text-gray-500 mb-1 block">Confirmation Number</label><Input value={h.confirmation_number} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], confirmation_number: e.target.value }; setHotelForm(v); }} data-testid={`hotel-conf-${i}`} /></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                    <div className="flex items-center gap-2 rounded-lg bg-white border border-gray-200 px-3 py-2">
                      <Switch checked={h.extra_bed_toggle} onCheckedChange={(c) => { const v = [...hotelForm]; v[i] = { ...v[i], extra_bed_toggle: c }; setHotelForm(v); }} data-testid={`hotel-eb-${i}`} />
                      <span className="text-sm text-gray-700 flex-1">Extra Bed</span>
                      {h.extra_bed_toggle && <Input type="number" min={0} className="w-20 h-8" value={h.extra_bed_count} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], extra_bed_count: parseInt(e.target.value) || 0 }; setHotelForm(v); }} />}
                    </div>
                    <div className="flex items-center gap-2 rounded-lg bg-white border border-gray-200 px-3 py-2">
                      <Switch checked={h.cwb_toggle} onCheckedChange={(c) => { const v = [...hotelForm]; v[i] = { ...v[i], cwb_toggle: c }; setHotelForm(v); }} data-testid={`hotel-cwb-${i}`} />
                      <span className="text-sm text-gray-700 flex-1">Child W/Bed</span>
                      {h.cwb_toggle && <Input type="number" min={0} className="w-20 h-8" value={h.cwb_count} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], cwb_count: parseInt(e.target.value) || 0 }; setHotelForm(v); }} />}
                    </div>
                    <div className="flex items-center gap-2 rounded-lg bg-white border border-gray-200 px-3 py-2">
                      <Switch checked={h.cnb_toggle} onCheckedChange={(c) => { const v = [...hotelForm]; v[i] = { ...v[i], cnb_toggle: c }; setHotelForm(v); }} data-testid={`hotel-cnb-${i}`} />
                      <span className="text-sm text-gray-700 flex-1">Child N/Bed</span>
                      {h.cnb_toggle && <Input type="number" min={0} className="w-20 h-8" value={h.cnb_count} onChange={e => { const v = [...hotelForm]; v[i] = { ...v[i], cnb_count: parseInt(e.target.value) || 0 }; setHotelForm(v); }} />}
                    </div>
                  </div>
                </div>
              ))}
              <Button variant="outline" className="w-full border-dashed" onClick={() => setHotelForm([...hotelForm, { hotel_name: '', city: '', hotel_category: '', room_type: 'Standard', meal_plan: '', nights: 1, check_in: '', check_out: '', rooms_count: 1, extra_bed_toggle: false, extra_bed_count: 0, cwb_toggle: false, cwb_count: 0, cnb_toggle: false, cnb_count: 0, confirmation_number: '' }])} data-testid="add-hotel-row-btn">
                + Add another hotel
              </Button>
            </div>
            <div className="p-5 border-t border-gray-100 flex justify-end gap-2 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={() => setEditingHotels(false)}>Cancel</Button>
              <Button onClick={saveHotelDetails} disabled={savingHotels} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-hotel-btn">
                <Save className="w-4 h-4 mr-2" /> {savingHotels ? 'Saving...' : 'Save Hotel Details'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default BookingDetail;
